import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Star, Shield, ChevronLeft, ChevronRight, Eye, Calendar, Weight, Target, Layers, AlertTriangle, ShoppingBag, ArrowLeft, Heart, Tag, Flag, X, Share2, MessageCircle, Send, ZoomIn, MapPin } from 'lucide-react';
import { listingsAPI, photoAPI, offersAPI, reportsAPI, messagesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ShippingEstimate } from '../components/ShippingEstimate';
import { useFavorites } from '../context/FavoritesContext';
import { useNotifications } from '../context/NotificationsContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import PhotoViewer from '../components/PhotoViewer';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const DAMAGE_KEYS = {
  scratches: 'listing.dmg_scratches', dents: 'listing.dmg_dents', paint_peeling: 'listing.dmg_paint',
  grip_worn: 'listing.dmg_grip', surface_cracks: 'listing.dmg_cracks', edge_damage: 'listing.dmg_edge',
  vibration: 'listing.dmg_vibration', none: 'listing.dmg_none',
};
const SHAPE_KEYS = { ronde: 'cat.shape_round', diamant: 'cat.shape_diamond', goutte: 'cat.shape_teardrop' };
const PLAY_KEYS = { control: 'cat.type_control', puissance: 'cat.type_power', polyvalent: 'cat.type_hybrid' };

const ListingDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { refresh: refreshNotifs } = useNotifications();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [showOffer, setShowOffer] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [sendingOffer, setSendingOffer] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [sendingReport, setSendingReport] = useState(false);
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [firstMessage, setFirstMessage] = useState('');
  const [sendingFirstMsg, setSendingFirstMsg] = useState(false);
  const [acceptedOffer, setAcceptedOffer] = useState(null);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);

  useEffect(() => {
    listingsAPI.getOne(id).then(res => { setListing(res.data); setLoading(false); })
      .catch(() => { setLoading(false); toast.error(i18n.t('listing.not_found')); navigate('/catalogue'); });
  }, [id, navigate]);

  // Fetch accepted offer for current user (if authenticated)
  useEffect(() => {
    if (isAuthenticated && id) {
      offersAPI.getAccepted(id).then(res => {
        setAcceptedOffer(res.data.accepted_offer);
      }).catch(() => {});
    }
  }, [isAuthenticated, id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7FAFB]">
      <div className="w-12 h-12 border-4 border-[#0C63BF] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!listing) return null;

  const photos = listing.photos?.map(pid => photoAPI.getUrl(pid)) || [];
  const isOwner = user?.id === listing.seller_id;
  const seller = listing.seller;
  const condScore = listing.condition_score;
  const condLabel = t(condScore >= 9 ? 'home.cond_new' : condScore >= 7 ? 'home.cond_very_good' : condScore >= 5 ? 'home.cond_good' : 'home.cond_fair');
  const condBarColor = condScore >= 8 ? 'bg-emerald-500' : condScore >= 5 ? 'bg-sky-500' : 'bg-orange-500';

  return (
    <div className="min-h-screen bg-[#F7FAFB]">
      <SEO
        title={`${listing.brand} ${listing.model} d'occasion - ${listing.price}€`}
        description={`Raquette de padel ${listing.brand} ${listing.model} d'occasion à ${listing.price}€ sur PadSmash. ${condLabel}. Paiement sécurisé, livraison suivie. ${listing.description?.slice(0, 100) || ''}`}
        path={`/annonce/${id}`}
        type="product"
        image={photos[0]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": `${listing.brand} ${listing.model}`,
          "description": listing.description,
          "image": photos[0],
          "brand": { "@type": "Brand", "name": listing.brand },
          "offers": {
            "@type": "Offer",
            "price": listing.price,
            "priceCurrency": "EUR",
            "availability": listing.status === 'active' ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
            "itemCondition": "https://schema.org/UsedCondition"
          }
        }}
      />
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <Link to="/catalogue" className="inline-flex items-center gap-1.5 text-sm text-[#5A8BA8] hover:text-[#0C63BF] transition-colors font-medium" data-testid="back-to-catalogue">
            <ArrowLeft className="w-4 h-4" /> {t('listing.back')}
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
          {/* Photos */}
          <div className="md:col-span-3 space-y-3">
            <div 
              className="bg-white rounded-2xl border border-[#E8F4FD] overflow-hidden aspect-[4/3] relative group cursor-pointer"
              onClick={() => photos.length > 0 && setShowPhotoViewer(true)}
              data-testid="photo-container"
            >
              {photos.length > 0 ? (
                <>
                  <img src={photos[currentPhoto]} alt={listing.model} className="w-full h-full object-contain bg-[#F7FAFB]" data-testid="listing-main-photo" />
                  
                  {/* Zoom hint overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 shadow-lg">
                      <ZoomIn className="w-5 h-5 text-[#0C63BF]" />
                      <span className="text-sm font-medium text-[#033871]">{t('listing.zoom')}</span>
                    </div>
                  </div>
                  
                  {photos.length > 1 && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); setCurrentPhoto(p => (p - 1 + photos.length) % photos.length); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-xl p-2.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white" data-testid="photo-prev">
                        <ChevronLeft className="w-5 h-5 text-[#033871]" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); setCurrentPhoto(p => (p + 1) % photos.length); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-xl p-2.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white" data-testid="photo-next">
                        <ChevronRight className="w-5 h-5 text-[#033871]" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-[#033871]">
                        {currentPhoto + 1} / {photos.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#F0F7FA] text-[#B8D4E3]">
                  <svg className="w-24 h-24" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2.5 overflow-x-auto pb-1">
                {photos.map((url, i) => (
                  <button key={i} onClick={() => setCurrentPhoto(i)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 shrink-0 transition-all ${i === currentPhoto ? 'border-[#0C63BF] ring-2 ring-[#0C63BF]/20' : 'border-[#E8F4FD] hover:border-[#B8D4E3]'}`}
                    data-testid={`photo-thumb-${i}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info panel */}
          <div className="md:col-span-2 space-y-4">
            {/* Price card */}
            <div className="bg-white rounded-2xl border border-[#E8F4FD] p-6" data-testid="price-card">
              <p className="text-[10px] text-[#5A8BA8] uppercase tracking-wider font-semibold">{listing.brand}</p>
              <h1 className="text-xl font-black text-[#033871] mt-1" data-testid="listing-title">{listing.model.replace(new RegExp('^' + listing.brand + '\\s+', 'i'), '')}</h1>
              {(listing.approved_at || listing.created_at) && (
                <p className="text-[11px] text-[#B8D4E3] mt-1" data-testid="listing-date">
                  {t('listing.published')} {(() => {
                    const d = listing.approved_at || listing.created_at;
                    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
                    if (diff < 60) return t('cat.just_now');
                    if (diff < 3600) return t('cat.min_ago', { n: Math.floor(diff / 60) });
                    if (diff < 86400) return t('cat.hours_ago', { n: Math.floor(diff / 3600) });
                    if (diff < 2592000) return t('listing.days_ago_long', { n: Math.floor(diff / 86400) });
                    return `${t('listing.published_on')} ${new Date(d).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : `${i18n.language}-${i18n.language.toUpperCase()}`, { day: 'numeric', month: 'long', year: 'numeric' })}`;
                  })()}
                </p>
              )}
              <div className="flex items-baseline gap-3 mt-3">
                <span className="text-3xl font-black text-[#0C63BF]" data-testid="listing-price">{listing.price.toFixed(0)}&euro;</span>
              </div>
              {isAuthenticated && !isOwner && <ShippingEstimate sellerId={listing.seller_id} user={user} />}
              
              {/* Price level indicator */}
              {listing.market_price && listing.market_price > 0 && (
                <div className="mt-3" data-testid="price-level-indicator">
                  {(() => {
                    const ratio = listing.price / listing.market_price;
                    let text, textColor, bgColor;
                    if (ratio <= 0.85) {
                      text = t('listing.price_very_good'); textColor = 'text-green-700'; bgColor = 'bg-green-100';
                    } else if (ratio <= 0.95) {
                      text = t('listing.price_good'); textColor = 'text-lime-700'; bgColor = 'bg-lime-100';
                    } else if (ratio <= 1.05) {
                      text = t('listing.price_ok'); textColor = 'text-yellow-700'; bgColor = 'bg-yellow-100';
                    } else if (ratio <= 1.15) {
                      text = t('listing.price_high'); textColor = 'text-orange-700'; bgColor = 'bg-orange-100';
                    } else {
                      text = t('listing.price_very_high'); textColor = 'text-red-700'; bgColor = 'bg-red-100';
                    }
                    const position = Math.min(Math.max((ratio - 0.7) / 0.6 * 100, 0), 100);
                    return (
                      <div>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-[#5A8BA8]">{t('listing.market_price', { price: listing.market_price })}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${bgColor} ${textColor}`}>{text}</span>
                        </div>
                        <div className="h-2 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-full relative">
                          <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white border-2 border-[#033871] rounded-full shadow-md" style={{ left: `calc(${position}% - 7px)` }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {acceptedOffer && (
                <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="negotiated-price-badge">
                  <p className="text-xs font-semibold text-emerald-700">
                    {acceptedOffer.buyer_id === user?.id ? t('listing.your_offer_accepted') : t('listing.offer_accepted')}
                  </p>
                  <p className="text-xl font-black text-emerald-600">{acceptedOffer.amount.toFixed(0)}&euro;</p>
                  <p className="text-[10px] text-emerald-600/70">{acceptedOffer.buyer_id === user?.id ? t('listing.price_visible_seller') : t('listing.price_visible_buyer')}</p>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                {!isOwner && (
                  <button onClick={() => { if (!isAuthenticated) { navigate('/login'); return; } toggleFavorite(listing.id); }}
                    className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-xl border-2 font-bold text-sm transition-all duration-200 ${isFavorite(listing.id) ? 'border-red-200 bg-red-50 text-red-500' : 'border-[#E8F4FD] hover:border-red-200 hover:bg-red-50 text-[#5A8BA8] hover:text-red-500'}`}
                    data-testid="detail-fav-btn">
                    <Heart className={`w-4 h-4 transition-colors ${isFavorite(listing.id) ? 'fill-red-500 text-red-500' : ''}`} />
                    {t('listing.favorites')}
                  </button>
                )}
                <button onClick={async () => {
                    // Use the OG share URL for rich previews
                    const shareUrl = `https://padsmash.com/api/share/listing/${listing.id}`;
                    const directUrl = `https://padsmash.com/annonce/${listing.id}`;
                    const shareData = {
                      title: `${listing.brand} ${listing.model} - ${listing.price}€`,
                      text: t('listing.share_text', { brand: listing.brand, model: listing.model, price: listing.price }),
                      url: shareUrl
                    };
                    
                    // Use native share API if available (mobile)
                    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                      try {
                        await navigator.share(shareData);
                      } catch (err) {
                        if (err.name !== 'AbortError') {
                          // User cancelled, do nothing
                          navigator.clipboard.writeText(shareUrl);
                          toast.success(t('listing.link_copied'));
                        }
                      }
                    } else {
                      // Fallback to clipboard
                      navigator.clipboard.writeText(shareUrl).then(() => {
                        toast.success(t('listing.link_copied'));
                      }).catch(() => {
                        toast.error(t('listing.link_copy_error'));
                      });
                    }
                  }}
                  className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl border-2 border-[#E8F4FD] hover:border-[#0C63BF] hover:bg-[#F0F7FA] text-[#5A8BA8] hover:text-[#0C63BF] font-bold text-sm transition-all duration-200"
                  data-testid="share-listing-btn">
                  <Share2 className="w-4 h-4" /> {t('listing.share')}
                </button>
              </div>

              {!isOwner && listing.status === 'active' && (
                <div className="mt-5">
                  {/* Direct purchase - Vinted style */}
                  <Button 
                    onClick={() => { 
                      if (!isAuthenticated) { navigate('/login'); return; }
                      navigate(`/checkout/${listing.id}`);
                    }}
                    className="w-full bg-[#32CD32] hover:bg-[#28a428] h-12 text-base gap-2 rounded-xl font-bold shadow-lg shadow-[#32CD32]/20" data-testid="buy-btn">
                    <ShoppingBag className="w-5 h-5" /> 
                    {acceptedOffer ? t('listing.buy_at', { price: acceptedOffer.amount.toFixed(0) }) : t('listing.buy_now')}
                  </Button>
                  <p className="text-[10px] text-center text-[#B8D4E3] mt-2">{t('listing.secure_note')}</p>
                  
                  {/* Offer section */}
                  {!acceptedOffer && (
                    !showOffer ? (
                      <button onClick={() => { if (!isAuthenticated) { navigate('/login'); return; } setShowOffer(true); }}
                        className="w-full h-11 mt-4 rounded-xl border-2 border-[#0C63BF] text-[#0C63BF] hover:bg-[#0C63BF] hover:text-white font-bold text-sm flex items-center justify-center gap-2 transition-all"
                        data-testid="make-offer-btn">
                        <Tag className="w-4 h-4" /> {t('listing.make_offer')}
                      </button>
                    ) : (
                      <div className="bg-[#F7FAFB] rounded-xl p-4 space-y-3 animate-fadeInUp border border-[#E8F4FD] mt-4" data-testid="offer-form">
                        <p className="text-sm font-bold text-[#033871]">{t('listing.propose_price')}</p>
                        <div className="relative">
                          <input type="number" step="1" min="1" max={listing.price - 1} value={offerAmount}
                            onChange={e => setOfferAmount(e.target.value)}
                            placeholder={t('listing.max_price', { price: listing.price - 1 })}
                            className="w-full h-12 pl-4 pr-10 rounded-xl border-2 border-[#D6E8F0] text-xl font-black text-[#033871] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none"
                            data-testid="offer-amount-input" />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg text-[#5A8BA8] font-bold">&euro;</span>
                        </div>
                        {offerAmount && parseFloat(offerAmount) > 0 && (
                          <p className="text-xs text-[#5A8BA8]">
                            {t('listing.discount_note_1')}<span className="font-bold text-[#0C63BF]">{Math.round((1 - parseFloat(offerAmount) / listing.price) * 100)}%</span>{t('listing.discount_note_2')}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button onClick={() => { setShowOffer(false); setOfferAmount(''); }}
                            className="flex-1 h-10 rounded-xl border border-[#D6E8F0] text-sm font-medium text-[#5A8BA8] hover:bg-[#F0F7FA] transition-colors"
                            data-testid="offer-cancel-btn">{t('listing.cancel')}</button>
                          <button onClick={async () => {
                              const amt = parseFloat(offerAmount);
                              if (!amt || amt <= 0 || amt >= listing.price) { toast.error(t('listing.invalid_amount')); return; }
                              setSendingOffer(true);
                              try {
                                await offersAPI.create({ listing_id: listing.id, amount: amt });
                                toast.success(t('listing.offer_sent'));
                                setShowOffer(false); setOfferAmount('');
                                refreshNotifs();
                              } catch (err) { toast.error(err.response?.data?.detail || t('listing.error')); }
                              setSendingOffer(false);
                            }}
                            disabled={sendingOffer || !offerAmount || parseFloat(offerAmount) <= 0 || parseFloat(offerAmount) >= listing.price}
                            className="flex-1 h-10 rounded-xl bg-[#32CD32] text-white text-sm font-bold hover:bg-[#28a428] disabled:opacity-50 transition-colors"
                            data-testid="offer-send-btn">
                            {sendingOffer ? t('listing.sending') : t('listing.send_offer')}
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
              {/* Contact seller button + popup */}
              {!isOwner && listing.status === 'active' && (
                <>
                  <button onClick={() => {
                      if (!isAuthenticated) { navigate('/login'); return; }
                      setShowMessagePopup(true);
                    }}
                    className="w-full h-11 mt-3 rounded-xl border-2 border-[#D6E8F0] text-[#5A8BA8] hover:border-[#0C63BF] hover:text-[#0C63BF] font-medium text-sm flex items-center justify-center gap-2 transition-all"
                    data-testid="contact-seller-btn">
                    <MessageCircle className="w-4 h-4" /> {t('listing.chat_seller')}
                  </button>
                  {showMessagePopup && (
                    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowMessagePopup(false)}>
                      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="message-popup">
                        <div className="flex items-center justify-between px-5 pt-5 pb-3">
                          <h3 className="font-bold text-[#033871] text-base">{t('listing.send_message')}</h3>
                          <button onClick={() => setShowMessagePopup(false)} className="p-1 rounded-lg hover:bg-[#F0F7FA]"><X className="w-4 h-4 text-[#5A8BA8]" /></button>
                        </div>
                        <div className="flex items-center gap-3 px-5 pb-3 border-b border-[#E8F4FD]">
                          {listing.photos?.[0] && <img src={photoAPI.getUrl(listing.photos[0])} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                          <div>
                            <p className="text-sm font-semibold text-[#033871]">{listing.brand} {listing.model}</p>
                            <p className="text-xs text-[#0C63BF] font-bold">{listing.price} &euro;</p>
                          </div>
                        </div>
                        <div className="p-5">
                          <textarea
                            value={firstMessage}
                            onChange={e => setFirstMessage(e.target.value)}
                            placeholder={t('listing.message_placeholder')}
                            className="w-full h-28 px-4 py-3 rounded-xl border border-[#D6E8F0] text-sm resize-none focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none"
                            autoFocus
                            data-testid="first-message-input"
                          />
                          <button
                            onClick={async () => {
                              if (!firstMessage.trim()) return;
                              setSendingFirstMsg(true);
                              try {
                                const r = await messagesAPI.createConversation(listing.id);
                                await messagesAPI.sendMessage(r.data.id, firstMessage.trim());
                                setShowMessagePopup(false);
                                setFirstMessage('');
                                toast.success(t('listing.message_sent'));
                                navigate('/dashboard?tab=messages');
                              } catch (err) { toast.error(err.response?.data?.detail || t('listing.error')); }
                              finally { setSendingFirstMsg(false); }
                            }}
                            disabled={!firstMessage.trim() || sendingFirstMsg}
                            className="w-full h-11 mt-3 rounded-xl bg-[#0C63BF] hover:bg-[#033871] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                            data-testid="send-first-message-btn">
                            <Send className="w-4 h-4" /> {sendingFirstMsg ? t('listing.sending') : t('listing.send')}
                          </button>
                          <p className="text-[10px] text-[#B8D4E3] text-center mt-2">{t('listing.masked_note')}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              {listing.status === 'sold' && (
                <div className="mt-5 bg-red-50 text-red-600 rounded-xl p-3.5 text-center font-bold text-sm" data-testid="sold-badge">{t('listing.sold')}</div>
              )}
            </div>

            {/* Specs */}
            <div className="bg-white rounded-2xl border border-[#E8F4FD] p-6" data-testid="specs-card">
              <h3 className="font-bold text-[#033871] mb-4">{t('listing.specs')}</h3>
              <div className="space-y-3">
                {[
                  { icon: Layers, label: t('listing.shape'), value: SHAPE_KEYS[listing.shape] ? t(SHAPE_KEYS[listing.shape]) : listing.shape },
                  { icon: Weight, label: t('listing.weight'), value: listing.weight },
                  { icon: Target, label: t('listing.play_type'), value: PLAY_KEYS[listing.play_type] ? t(PLAY_KEYS[listing.play_type]) : listing.play_type },
                  { icon: Calendar, label: t('listing.purchase_year'), value: listing.year_of_purchase },
                ].map((spec, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#F0F7FA] last:border-0">
                    <div className="flex items-center gap-2.5 text-sm text-[#5A8BA8]">
                      <spec.icon className="w-4 h-4" />
                      <span>{spec.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#033871]">{spec.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-[#F0F7FA]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-[#033871]">{t('listing.condition')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#033871]">{condScore}/10</span>
                    <span className="text-[10px] font-bold text-white bg-[#0C63BF] px-2 py-0.5 rounded-full">{condLabel}</span>
                  </div>
                </div>
                <div className="w-full h-2 bg-[#F0F7FA] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${condBarColor}`} style={{ width: `${condScore * 10}%` }} />
                </div>
                {listing.damages?.length > 0 && !listing.damages.includes('none') && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {listing.damages.map(d => (
                      <span key={d} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-orange-50 text-orange-600 border border-orange-100">
                        <AlertTriangle className="w-3 h-3" /> {DAMAGE_KEYS[d] ? t(DAMAGE_KEYS[d]) : d}
                      </span>
                    ))}
                  </div>
                )}
                {listing.damages?.includes('none') && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100 mt-3">
                    <Shield className="w-3 h-3" /> {t('listing.no_damage')}
                  </span>
                )}
              </div>
            </div>

            {/* Seller */}
            {seller && (
              <Link to={`/vendeur/${seller.id}`} className="block" data-testid="seller-card">
                <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5 hover:border-[#0C63BF] hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#0C63BF] rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {seller.first_name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#033871] text-sm">{seller.first_name} {seller.last_name}</p>
                      <p className="flex items-center gap-1 text-xs text-[#5A8BA8] mt-0.5" data-testid="seller-country">
                        <MapPin className="w-3 h-3" /> {t('listing.ships_from', { country: (() => { try { return new Intl.DisplayNames([i18n.language], { type: 'region' }).of(seller.country || 'FR'); } catch { return seller.country || 'FR'; } })() })}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {seller.avg_rating ? (
                          <>
                            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                            <span className="text-xs font-semibold text-[#033871]">{seller.avg_rating.toFixed(1)}</span>
                            <span className="text-[10px] text-[#5A8BA8]">{t('listing.seller_reviews_count', { n: seller.rating_count })}</span>
                          </>
                        ) : (
                          <span className="text-xs text-[#5A8BA8]">{t('home.verified_profile')}</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#B8D4E3] shrink-0" />
                  </div>
                </div>
              </Link>
            )}

            {/* Report button */}
            {!isOwner && isAuthenticated && (
              <button
                onClick={() => setShowReport(true)}
                className="w-full flex items-center justify-center gap-2 h-10 rounded-xl border border-[#E8F4FD] text-[#5A8BA8] hover:text-red-500 hover:border-red-200 hover:bg-red-50 text-sm font-medium transition-all"
                data-testid="report-listing-btn"
              >
                <Flag className="w-4 h-4" /> {t('listing.report')}
              </button>
            )}
          </div>
        </div>

        {listing.description && (
          <div className="bg-white rounded-2xl border border-[#E8F4FD] p-6 mt-6" data-testid="description-card">
            <h3 className="font-bold text-[#033871] mb-3">{t('listing.description')}</h3>
            <p className="text-[#5A8BA8] text-sm whitespace-pre-wrap leading-relaxed">{listing.description}</p>
          </div>
        )}

        {listing.seller_ratings?.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E8F4FD] p-6 mt-6" data-testid="ratings-card">
            <h3 className="font-bold text-[#033871] mb-5">{t('listing.seller_reviews')}</h3>
            <div className="space-y-4">
              {listing.seller_ratings.map(r => (
                <div key={r.id} className="border-b border-[#F0F7FA] pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-[#E8F4FD]'}`} />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-[#033871]">{r.buyer_name}</span>
                  </div>
                  {r.comment && <p className="text-sm text-[#5A8BA8] mt-1.5 leading-relaxed">{r.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="report-modal-overlay" onClick={() => setShowReport(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative animate-fadeInUp" onClick={e => e.stopPropagation()} data-testid="report-modal">
            <button onClick={() => setShowReport(false)} className="absolute top-4 right-4 text-[#5A8BA8] hover:text-[#033871]" data-testid="report-modal-close">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <Flag className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-bold text-[#033871]">{t('listing.report')}</h3>
                <p className="text-xs text-[#5A8BA8]">{listing.brand} {listing.model}</p>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-[#033871]">{t('listing.report_reason')}</p>
              {[
                { value: 'fraudulent', label: t('listing.r_fraudulent') },
                { value: 'misleading_photos', label: t('listing.r_photos') },
                { value: 'abusive_price', label: t('listing.r_price') },
                { value: 'inappropriate', label: t('listing.r_inappropriate') },
                { value: 'other', label: t('listing.r_other') },
              ].map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${reportReason === opt.value ? 'border-[#0C63BF] bg-[#F0F7FA]' : 'border-[#E8F4FD] hover:border-[#B8D4E3]'}`}
                  data-testid={`report-reason-${opt.value}`}
                >
                  <input
                    type="radio"
                    name="report_reason"
                    value={opt.value}
                    checked={reportReason === opt.value}
                    onChange={e => setReportReason(e.target.value)}
                    className="accent-[#0C63BF]"
                  />
                  <span className="text-sm text-[#033871] font-medium">{opt.label}</span>
                </label>
              ))}

              <textarea
                value={reportDetails}
                onChange={e => setReportDetails(e.target.value)}
                placeholder={t('listing.report_details')}
                rows={3}
                className="w-full rounded-xl border-2 border-[#E8F4FD] p-3 text-sm text-[#033871] placeholder:text-[#B8D4E3] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none resize-none"
                data-testid="report-details-input"
              />

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { setShowReport(false); setReportReason(''); setReportDetails(''); }}
                  className="flex-1 h-11 rounded-xl border border-[#D6E8F0] text-sm font-medium text-[#5A8BA8] hover:bg-[#F0F7FA] transition-colors"
                  data-testid="report-cancel-btn"
                >
                  {t('listing.cancel')}
                </button>
                <button
                  onClick={async () => {
                    if (!reportReason) { toast.error(t('listing.select_reason')); return; }
                    setSendingReport(true);
                    try {
                      await reportsAPI.create({ listing_id: listing.id, reason: reportReason, details: reportDetails });
                      toast.success(t('listing.report_sent'));
                      setShowReport(false); setReportReason(''); setReportDetails('');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || t('listing.report_error'));
                    }
                    setSendingReport(false);
                  }}
                  disabled={sendingReport || !reportReason}
                  className="flex-1 h-11 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-50 transition-colors"
                  data-testid="report-submit-btn"
                >
                  {sendingReport ? t('listing.sending') : t('listing.send_report')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {showPhotoViewer && photos.length > 0 && (
        <PhotoViewer
          photos={photos}
          initialIndex={currentPhoto}
          onClose={() => setShowPhotoViewer(false)}
        />
      )}
    </div>
  );
};

export default ListingDetailPage;
